import React, { useState, useEffect } from "react";
import {
  Card,
  List,
  Button,
  Modal,
  Form,
  Input,
  DatePicker,
  Popconfirm,
  Badge,
  Steps,
  Typography,
  Select,
  Option,
  message,
  Image,
  Divider,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CloseOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import moment from "moment";
import "moment/locale/es";

import dayjs from "dayjs";
import "dayjs/locale/es";

import { fetchDrivers } from "../utils/fetchDrivers";
import {
  fetchOrderByID,
  editDeliveryOrder,
  addDeliveryOrder,
  deleteDeliveryOrder,
  uploadDeliveryImage,
  deleteDeliveryImage,
} from "../utils/fetchOrders";
import DragAndDrop from "./DragAndDrop";
import { ThreeDot } from "react-loading-indicators";
import { useAuth } from "../contexts/AuthProvider";
import { hasPermission } from "../utils/auth";

const { Text } = Typography;
const { Step } = Steps;

const statusMap = {
  "Por entregar": "Por entregar",
  "En ruta": "En ruta",
  Entregado: "Entregado",
  "No entregado": "No entregado",
  "Parcialmente entregado": "Parcialmente entregado",
  Reagendado: "Reagendado",
};

const statusColors = {
  "Por entregar": "red",
  "En ruta": "orange",
  Entregado: "green",
  "No entregado": "gray",
  "Parcialmente entregado": "gold",
  Reagendado: "blue",
};

// Index into the 3-step "No Asignado / En Ruta / Entregado" Steps tracker below.
const statusStepIndex = {
  "Por entregar": 0,
  "En ruta": 1,
  Entregado: 2,
  "No entregado": 2,
  "Parcialmente entregado": 2,
  Reagendado: 2,
};

const DeliveryInfoCard = ({ deliveryInfo, setDeliveryInfo, orderID }) => {
  // console.log("esto es deliveryinfo", deliveryInfo);
  const { user } = useAuth();
  const canDeleteImages = hasPermission(user?.permissions, "deliveries:delete");
  // Delivery *records* (add/edit/delete) go through /orders/add|edit|delete/delivery,
  // which the backend gates on orders:write — distinct from deliveries:delete,
  // which only covers the photo-delete endpoint above.
  const canWriteOrders = hasPermission(user?.permissions, "orders:write");

  const [loadingDeliveryInfo, setLoadingDeliveryInfo] = useState(false);
  const [uploadingDelivery, setUploadingDelivery] = useState(null);
  const [showUploadFor, setShowUploadFor] = useState(null);
  // Read endpoints (GET /orders/:id, GET /deliveries/:id) only return image
  // URLs, not their delivery_images.id, so we can only offer delete for
  // photos uploaded this session — the upload response is the only place
  // an id is ever returned.
  const [uploadedImageIds, setUploadedImageIds] = useState({});
  const [deletingImageUrl, setDeletingImageUrl] = useState(null);

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingDelivery, setEditingDelivery] = useState(null);
  const [form] = Form.useForm();
  const selectedDeliveryStatus = Form.useWatch("delivery_status", form);
  const isDriverEditable = selectedDeliveryStatus === "Por entregar";

  const [drivers, setDrivers] = useState([]);

  const [options, setOptions] = useState([]);
  const [activeOptions, setActiveOptions] = useState([]);
  const [isDriverLoading, setIsDriverLoading] = useState(true);

  useEffect(async () => {
    dayjs.locale("es");
    const newOptions = await fetchDrivers();
    const toOption = (personnel) => ({
      id: personnel.personnel_id,
      value: personnel.personnel_id,
      label: personnel.name,
    });
    const byId = (a, b) => a.id - b.id;
    // `options` keeps every driver so already-assigned (now inactive) drivers
    // still display correctly; `activeOptions` is what's offered when picking one.
    setOptions(newOptions.map(toOption).sort(byId));
    setActiveOptions(
      newOptions.filter((personnel) => personnel.is_active).map(toOption).sort(byId),
    );
    setIsDriverLoading(false);
  }, []);

  const handlePhotoUpload = async (files, deliveryId) => {
    setUploadingDelivery(deliveryId);
    try {
      const newDeliveryInfo = [...deliveryInfo];
      const newUploadedImageIds = {};
      for (const file of files) {
        const { id: imageId, url } = await uploadDeliveryImage(
          deliveryId,
          file,
        );
        newUploadedImageIds[url] = imageId;
        const index = newDeliveryInfo.findIndex(
          (item) => item.delivery_id === deliveryId,
        );
        newDeliveryInfo[index] = {
          ...newDeliveryInfo[index],
          images: [...(newDeliveryInfo[index].images || []), url],
        };
      }
      setDeliveryInfo(newDeliveryInfo);
      setUploadedImageIds((prev) => ({ ...prev, ...newUploadedImageIds }));
      message.success("Foto subida correctamente.");
    } catch (error) {
      message.error("Error al subir la foto.");
    } finally {
      setUploadingDelivery(null);
      setShowUploadFor(null);
    }
  };

  const handleDeleteImage = async (deliveryId, url) => {
    const imageId = uploadedImageIds[url];
    if (!imageId) return;
    setDeletingImageUrl(url);
    try {
      await deleteDeliveryImage(imageId);
      const newDeliveryInfo = deliveryInfo.map((item) =>
        item.delivery_id === deliveryId
          ? { ...item, images: (item.images || []).filter((img) => img !== url) }
          : item,
      );
      setDeliveryInfo(newDeliveryInfo);
      setUploadedImageIds((prev) => {
        const next = { ...prev };
        delete next[url];
        return next;
      });
      message.success("Foto eliminada correctamente.");
    } catch (error) {
      message.error("Error al eliminar la foto.");
    } finally {
      setDeletingImageUrl(null);
    }
  };

  const handleAdd = () => {
    setEditingDelivery(null);
    form.resetFields();
    form.setFieldsValue({
      driver_id: 0,
      delivery_status: "Por entregar",
    });
    setIsModalVisible(true);
  };

  const handleEdit = (item) => {
    setEditingDelivery(item);
    console.log("item to be edited", item);
    form.setFieldsValue({
      ...item,
      driver_id: item?.driver_id || 0,
      delivery_date: item.delivery_date
        ? dayjs(item.delivery_date, "YYYY-MM-DD")
        : null,
    });
    setIsModalVisible(true);
  };

  const handleDelete = async (delivery_id) => {
    setLoadingDeliveryInfo(delivery_id);
    try {
      const val = await deleteDeliveryOrder(delivery_id);
      console.log("val de delete", val);
      const newDeliveryInfo = await fetchOrderByID(orderID);
      setDeliveryInfo(newDeliveryInfo[0].delivery_info_array);
    } catch (error) {
      console.log(error);
      message.error("Error al eliminar la entrega.");
    } finally {
      setLoadingDeliveryInfo(false);
    }
  };

  const [loadingModalOkButton, setLoadingModalOkButton] = useState(false);

  const handleModalOk = async () => {
    setLoadingModalOkButton(true);
    setLoadingDeliveryInfo(true);
    let newDeliveryInfo = [...deliveryInfo];
    try {
      const values = await form.validateFields();
      console.log("Received values of form: ", values);
      // console.log("this is new delivery info", newDeliveryInfo);
      if (editingDelivery) {
        // Editing a delivery
        console.log("this is editing delivery", editingDelivery);
        const index = newDeliveryInfo.findIndex(
          (item) => item.delivery_id === editingDelivery.delivery_id,
        );
        newDeliveryInfo[index] = {
          ...values,
          delivery_date: values.delivery_date.format("YYYY-MM-DD"),
          delivery_id: editingDelivery.delivery_id,
        };
        console.log("this is new delivery info index", newDeliveryInfo[index]);
        const val = await editDeliveryOrder(newDeliveryInfo[index]);
        // console.log(val);
        setDeliveryInfo(newDeliveryInfo);
        message.success("Entrega actualizada correctamente.");
        newDeliveryInfo = await fetchOrderByID(orderID);
        setDeliveryInfo(newDeliveryInfo[0].delivery_info_array);
      } else {
        // Adding a delivery
        // newDeliveryInfo.push({
        //   ...values,
        //   delivery_date: values.delivery_date.format("YYYY-MM-DD"),
        //   // delivery_id: Date.now(),
        // });
        // setDeliveryInfo(newDeliveryInfo);
        const postData = { ...values, order_id: orderID };
        console.log("estos son los valores", postData);
        const val = await addDeliveryOrder(postData);
        console.log("esto es val de created", val);
        message.success("Entrega actualizada correctamente.");
        newDeliveryInfo = await fetchOrderByID(orderID);
        setDeliveryInfo(newDeliveryInfo[0].delivery_info_array);
      }
      // console.log("this is the new delivery info", newDeliveryInfo);
    } catch (err) {
      message.error("Error al actualizar la entrega.");
      console.log("error", err);
    } finally {
      setLoadingModalOkButton(false);
      setIsModalVisible(false);
      setLoadingDeliveryInfo(false);
    }
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
  };

  return (
    <>
      <List
        grid={{ gutter: 16, column: 1 }}
        dataSource={deliveryInfo}
        renderItem={(item) => {
          const currentStatus = statusStepIndex[item.delivery_status] ?? 0;
          const isGray =
            item.delivery_status === "No entregado" ||
            item.delivery_status === "Reagendado";
          // console.log("currentStatus", currentStatus);
          return (
            <List.Item>
              <Card
                title={
                  <div className="flex items-center gap-2">
                    <span style={{ color: isGray ? "gray" : "inherit" }}>
                      Fecha de entrega:{" "}
                      {moment(item.delivery_date).format("LL")}
                    </span>
                    {(item.images || []).length > 0 && (
                      <Badge
                        count={(item.images || []).length}
                        style={{ backgroundColor: "#4048F1" }}
                      />
                    )}
                  </div>
                }
                extra={
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <Badge
                      status="default"
                      color={statusColors[item.delivery_status]}
                      text={item.delivery_status}
                    />
                    {canWriteOrders && (
                      <>
                        <Button
                          disabled={loadingDeliveryInfo}
                          type="link"
                          icon={<EditOutlined />}
                          onClick={() => handleEdit(item)}
                          style={{
                            marginLeft: 8,
                          }}
                        />
                        <Popconfirm
                          title={
                            <div className="flex flex-col">
                              <div>Esta acción no se puede deshacer.</div>
                              <div>¿Deseas continuar?</div>
                            </div>
                          }
                          onConfirm={() => handleDelete(item.delivery_id)}
                        >
                          <Button
                            type="link"
                            icon={<DeleteOutlined />}
                            style={{
                              marginLeft: 8,
                            }}
                            disabled={
                              deliveryInfo.length === 1 || loadingDeliveryInfo
                            }
                          />
                        </Popconfirm>
                      </>
                    )}
                  </div>
                }
              >
                <div
                  className="md:grid md:grid-cols-2 mb-4 flex-columns"
                  style={{ color: isGray ? "gray" : "inherit" }}
                >
                  <div>
                    <Text strong>Conductor: </Text>
                    {isDriverLoading ? (
                      <span className="text-gray-500">Cargando...</span>
                    ) : (
                      options.filter(
                        (driver) => driver.id === item.driver_id,
                      )[0].label || <span className="italic">No asignado</span>
                    )}
                  </div>
                  <div>
                    <Text strong>Observacion:</Text>{" "}
                    {item.delivery_note || (
                      <span className="italic">Sin Nota</span>
                    )}
                  </div>
                </div>
                <Steps current={currentStatus} size="small">
                  <Step
                    title={
                      <span
                        className={isGray && "italic text-gray-500"}
                      >
                        No Asignado
                      </span>
                    }
                  />
                  <Step
                    title={
                      <span
                        className={isGray && "italic text-gray-500"}
                      >
                        En Ruta
                      </span>
                    }
                  />
                  <Step
                    title={
                      isGray ? (
                        <span className="italic text-gray-500">
                          {item.delivery_status}
                        </span>
                      ) : (
                        "Entregado"
                      )
                    }
                    icon={
                      isGray ? (
                        <CloseCircleOutlined
                          style={{ color: "gray" }}
                          className="w-6 h-6"
                        />
                      ) : null
                    }
                  />
                </Steps>

                <Divider style={{ margin: "12px 0 8px" }} />

                <div>
                  <div className="flex justify-between items-center mb-2">
                    <Text strong>Fotos de Entrega</Text>
                    <Button
                      type="link"
                      size="small"
                      icon={<PlusOutlined />}
                      onClick={() =>
                        setShowUploadFor(
                          showUploadFor === item.delivery_id
                            ? null
                            : item.delivery_id,
                        )
                      }
                    >
                      Añadir
                    </Button>
                  </div>

                  {showUploadFor === item.delivery_id && (
                    <DragAndDrop
                      placeholder="Arrastra o haz clic para subir una foto"
                      onFiles={(files) =>
                        handlePhotoUpload(files, item.delivery_id)
                      }
                    />
                  )}

                  {uploadingDelivery === item.delivery_id && (
                    <div className="flex justify-center py-2">
                      <ThreeDot
                        variant="brick-stack"
                        color="#4048F1"
                        size="small"
                      />
                    </div>
                  )}

                  {(item.images || []).length > 0 ? (
                    <Image.PreviewGroup>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {(item.images || []).map((url, i) => (
                          <div
                            key={i}
                            className="relative group"
                            style={{ width: 80, height: 80 }}
                          >
                            <Image
                              width={80}
                              height={80}
                              src={url}
                              style={{ objectFit: "cover", borderRadius: 4 }}
                            />
                            {canDeleteImages && uploadedImageIds[url] && (
                              <Popconfirm
                                title="¿Eliminar esta foto?"
                                onConfirm={() =>
                                  handleDeleteImage(item.delivery_id, url)
                                }
                              >
                                <Button
                                  danger
                                  type="text"
                                  size="small"
                                  icon={<DeleteOutlined />}
                                  loading={deletingImageUrl === url}
                                  className="absolute top-0 right-0 opacity-0 group-hover:opacity-100 transition-opacity"
                                  style={{
                                    zIndex: 10,
                                    background: "rgba(255,255,255,0.85)",
                                    minWidth: 0,
                                    padding: "0 4px",
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              </Popconfirm>
                            )}
                          </div>
                        ))}
                      </div>
                    </Image.PreviewGroup>
                  ) : (
                    uploadingDelivery !== item.delivery_id && (
                      <Text type="secondary">
                        <span className="italic text-sm">Sin fotos</span>
                      </Text>
                    )
                  )}
                </div>
              </Card>
            </List.Item>
          );
        }}
      />
      {canWriteOrders && (
        <Button
          type="dashed"
          onClick={handleAdd}
          icon={<PlusOutlined />}
          style={{ width: "100%", marginTop: 16 }}
        >
          Añadir Entrega
        </Button>
      )}
      <Modal
        title={editingDelivery ? "Editar Entrega" : "Añadir Delivery"}
        visible={isModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        okText={editingDelivery ? "Guardar" : "Añadir"}
        confirmLoading={loadingModalOkButton}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{ driver_id: deliveryInfo[0].driver_id }}
        >
          <Form.Item
            name="delivery_date"
            label="Fecha de Entrega"
            rules={[
              {
                required: true,
                message: "Debes ingresar una fecha de entrega!",
              },
            ]}
          >
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            name="delivery_status"
            label="Estado de la entrega"
            rules={[
              {
                required: true,
                message: "Debes ingresar un Estado del Pedido!",
              },
            ]}
          >
            <Select>
              {Object.keys(statusMap).map((key) => (
                <Select.Option key={statusMap[key]} value={statusMap[key]}>
                  {key}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="driver_id" label="Chofer">
            <Select
              style={{ width: 150 }}
              disabled={!isDriverEditable}
              // onChange={onChange}
            >
              {activeOptions.map((item) => (
                <Select.Option value={item.value}>{item.label}</Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="delivery_note" label="Nota de Entrega">
            <Input autoComplete="off" onPressEnter={handleModalOk} />
          </Form.Item>

          <Button type="primary" htmlType="submit" className="hidden">
            Submit
          </Button>
        </Form>
      </Modal>
    </>
  );
};

export default DeliveryInfoCard;
